'use client';

import { motion } from "framer-motion";
import { Bot, Cpu, Lock, Zap } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";

const features = [
  {
    icon: Bot,
    title: "Advanced AI Models",
    description: "Access state-of-the-art AI models trained on diverse datasets"
  },
  {
    icon: Zap,
    title: "Real-time Processing",
    description: "Get instant results with our optimized processing pipeline"
  },
  {
    icon: Lock,
    title: "Enterprise Security",
    description: "Bank-grade security with data encryption and compliance"
  },
  {
    icon: Cpu,
    title: "Scalable Infrastructure",
    description: "Built on cloud-native architecture for unlimited scalability"
  }
];

export function FeaturesSection() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section className="w-full py-16 md:py-24">
      <div className="container">
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {features.map((feature) => (
            <motion.div
              key={feature.title}
              variants={itemVariants}
              className="relative group p-6 rounded-xl hover:bg-primary/5 transition-colors duration-300 flex flex-col items-center text-center"
            >
              <div className="space-y-4 flex flex-col items-center">
                <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">
                  {localizeSpelling(feature.title, currentLocale)}
                </h3>
                <p className="text-muted-foreground">
                  {localizeSpelling(feature.description, currentLocale)}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}